// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  useConnectionMutationBridge,
  useEntityFieldsBridge,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import {useAnalytics} from '@github-ui/use-analytics'
import {useEffect, useMemo} from 'react'
import type {Environment} from 'react-relay'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'relay-runtime'

import {
  type IssueViewerLiveUpdateChannels,
  issueViewerSecondaryViewQueryKey,
  issueViewerSecondaryViewQueryOptions,
  type IssueViewerSecondaryViewResponse,
} from './fetch-issue-viewer-secondary-view'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import {
  BLOCKED_BY_CONNECTION_PATH,
  BLOCKING_CONNECTION_PATH,
} from './sections/relations-section/dependency-issue-query-types'
import {RELATES_TO_CONNECTION_PATH} from './sections/relations-section/relates-to-query-types'

const SUB_ISSUES_CONNECTION_PATH = ['subIssues']
const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

type UseSecondaryQueryProps = {
  owner: string
  repo: string
  number: number
  markAsRead?: boolean
}

export function resolveHideClosedSubIssuesEnabled(
  serverFeatures: IssueViewerSecondaryViewResponse['features'] | undefined,
  clientFeatureEnabled: boolean,
): boolean {
  return serverFeatures?.hideClosedSubIssues ?? clientFeatureEnabled
}

function markIssueRead(environment: Environment, issueId: string) {
  return commitLocalUpdate(environment, store => {
    const issue = store.get(issueId)
    if (issue) {
      issue.setValue(true, 'isReadByViewer')
    }
  })
}

export function useSecondaryQuery({
  owner,
  repo,
  number,
  markAsRead = true,
}: UseSecondaryQueryProps): [
  IssueSecondaryNode | null | undefined,
  boolean,
  IssueViewerLiveUpdateChannels | undefined,
] {
  const clientHideClosedSubIssuesEnabled = isFeatureEnabled('issues_hide_closed_sub_issues')
  const {sendAnalyticsEvent} = useAnalytics()
  const environment = useRelayEnvironment()

  // markAsRead is excluded from the query key because it triggers a server side effect
  // (clearing the unread notification); including it would let a cached response satisfy
  // the request, so the notification would never get marked as read.
  const secondaryViewQueryKey = issueViewerSecondaryViewQueryKey(owner, repo, number)
  const secondaryViewQueryOptions = useMemo(
    () => issueViewerSecondaryViewQueryOptions({owner, repo, number, markAsRead, sendAnalyticsEvent}),
    [markAsRead, number, owner, repo, sendAnalyticsEvent],
  )
  const {data: tanstackSecondaryViewData} = useQuery(secondaryViewQueryOptions)

  // Entity field edits arrive as operations naming their own target, so the overlay finds every copy
  // this document holds — the issue, its parent, its sub-issue rows, its milestone.
  useEntityFieldsBridge()

  // Overlay in-flight connection mutations onto this query's data once the secondary view resolves.
  const overlayIssueId = tanstackSecondaryViewData?.repository?.issue?.id
  const optimisticOverlayEnabled = overlayIssueId != null
  // Owner id points at `repository.issue`; the engine finds it by id anywhere in `base`, so the
  // connection path stays `['subIssues']`. `?? ''` only covers disabled passes (never a live owner).
  const subIssuesOwnerId = overlayIssueId ?? ''

  // Mirrors legacy Relay producers into the mutation cache for the overlay below.
  useConnectionMutationBridge({
    ownerId: subIssuesOwnerId,
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    enabled: optimisticOverlayEnabled,
  })

  // Opt-in per connection: insert positions and count deltas only mean something where it renders.
  const optimisticConnections = useMemo(() => {
    if (!overlayIssueId) return []
    return [
      {ownerId: overlayIssueId, connectionPath: RELATES_TO_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: BLOCKED_BY_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: BLOCKING_CONNECTION_PATH},
      {ownerId: overlayIssueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH},
      ...(optimisticOverlayEnabled ? [{ownerId: subIssuesOwnerId, connectionPath: SUB_ISSUES_CONNECTION_PATH}] : []),
    ]
  }, [optimisticOverlayEnabled, overlayIssueId, subIssuesOwnerId])
  // No `revalidate`: `SubIssue::MAXIMUM_BREADTH` caps a parent at 100, so `subIssues(first: 100)` is
  // never a partial page and the fold over the remove operation's deltas is already authoritative.
  const secondaryViewTargets = useMemo(() => [{queryKey: secondaryViewQueryKey}], [secondaryViewQueryKey])
  const mergedSecondaryViewData = useOptimisticData(tanstackSecondaryViewData, {
    // Entity operations need no connection list.
    connections: optimisticConnections,
    targets: secondaryViewTargets,
  })

  const secondaryIssueData: IssueSecondaryNode | null | undefined = mergedSecondaryViewData?.repository?.issue
  const hideClosedSubIssuesEnabled = resolveHideClosedSubIssuesEnabled(
    tanstackSecondaryViewData?.features,
    clientHideClosedSubIssuesEnabled,
  )
  const issueId = secondaryIssueData && secondaryIssueData.id

  useEffect(() => {
    if (markAsRead && issueId) {
      markIssueRead(environment, issueId)
    }
  }, [environment, markAsRead, issueId])

  const liveUpdateChannels = tanstackSecondaryViewData?.channels

  return [secondaryIssueData, hideClosedSubIssuesEnabled, liveUpdateChannels]
}
